import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  Avatar, MobileList, MobileListEmpty, MobileListError, MobileListSkeleton, MobileRow, StatusPill, relativeTime,
} from '../kit';
import { ROLE_LABELS, displayName, isDeactivated, orgRoleChips, shortDate, userStatus } from './userModel';

// Phones only show a pill when the status is worth a glance.
const PHONE_PILL = {
  invited: { tone: 'info', label: 'Invited' },
  deactivated: { tone: 'off', label: 'Deactivated' },
};

const roleLabel = (role) => ROLE_LABELS[role] || role || 'No role';

/**
 * "Org admin · Northwind" for one org; "Org admin · 2 orgs" when every org
 * shares the role, "2 orgs · mixed roles" when they don't.
 */
export function compactRoleChip(chips) {
  if (!chips.length) return null;
  if (chips.length === 1) return { main: roleLabel(chips[0].role), rest: chips[0].name };
  const roles = new Set(chips.map((c) => c.role || ''));
  return roles.size === 1
    ? { main: roleLabel(chips[0].role), rest: `${chips.length} orgs` }
    : { main: `${chips.length} orgs`, rest: 'mixed roles' };
}

function compactSignIn(user, status) {
  if (user.lastSignInAt) return relativeTime(user.lastSignInAt);
  if (status === 'invited') {
    const when = shortDate(user.lastInvitedAt || user.invitedAt);
    return when ? `Invited ${when}` : null;
  }
  return null;
}

/**
 * Phone version of UsersTable: one card row per user. Tapping the row opens
 * the user panel; the 44px "…" button opens the action sheet.
 *   onOpen(user) · onActions(user)
 */
export default function UsersCardList({
  rows, loading, loaded, error, onRetry, onOpen, onActions, orgNameByCode, emptyState,
}) {
  return (
    <MobileList label="Users" busy={loading && loaded}>
      {!loaded && loading ? <MobileListSkeleton rows={8} /> : null}
      {error && !loading ? <MobileListError onRetry={onRetry}>Couldn't load users. {error}</MobileListError> : null}
      {loaded && !error && !rows.length && !loading ? (
        <MobileListEmpty title={emptyState.title} body={emptyState.body} action={emptyState.action} />
      ) : null}
      {rows.map((u) => {
        const status = u.adminMeta?.status || userStatus(u);
        const pill = PHONE_PILL[status];
        const name = displayName(u);
        const chip = compactRoleChip(orgRoleChips(u, orgNameByCode));
        const signIn = compactSignIn(u, status);
        return (
          <MobileRow
            key={u.id}
            faded={isDeactivated(u)}
            onClick={() => onOpen(u)}
            lead={<Avatar as="span" name={name} email={u.email} />}
            title={name}
            sub={(
              <>
                {chip ? (
                  <span className="ak-role ak-m-shrink">{chip.main} <span>· {chip.rest}</span></span>
                ) : (
                  <span className="ak-m-shrink">No organizations</span>
                )}
                {signIn ? <span aria-hidden="true">·</span> : null}
                {signIn ? <span title={u.lastSignInAt ? `Last sign-in ${new Date(u.lastSignInAt).toLocaleString()}` : undefined}>{signIn}</span> : null}
              </>
            )}
            aside={pill ? <StatusPill tone={pill.tone}>{pill.label}</StatusPill> : null}
            trailing={(
              <button
                type="button"
                className="ak-m-more"
                aria-label={`Actions for ${name}`}
                aria-haspopup="dialog"
                onClick={() => onActions(u)}
              >
                <MoreHorizontal aria-hidden="true" />
              </button>
            )}
          />
        );
      })}
    </MobileList>
  );
}
